import { Dialog } from "@components/ui/Dialog";
import { useCsvExport } from "@lib/hooks/useCsvExport";
import { Download } from "lucide-react";
import { useState } from "react";

/**
 * Modal for CSV export with column selection
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Array} props.data - Data to export
 * @param {Array} props.columns - Column definitions
 * @param {string} props.filename - Default filename
 * @param {Array} props.selectedRows - Specific rows to export (optional)
 */
export function CsvExportModal({
  isOpen,
  onClose,
  data,
  columns,
  filename = "export.csv",
  selectedRows,
}) {
  const { exportCsv, isExporting, availableColumns } = useCsvExport({
    data,
    columns,
    filename,
  });

  const [selectedColumns, setSelectedColumns] = useState(availableColumns.map((col) => col.id));

  const handleToggleColumn = (columnId) => {
    setSelectedColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
    );
  };

  const handleSelectAll = () => {
    setSelectedColumns(availableColumns.map((col) => col.id));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const handleExport = () => {
    exportCsv({
      selectedColumns,
      selectedData: selectedRows,
    });
    onClose();
  };

  const previewData = selectedRows || data?.slice(0, 3) || [];

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Exporter en CSV"
      icon={<Download className="w-5 h-5 text-primary" />}
      size="lg"
      dismissable={!isExporting}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isExporting}>
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-primary gap-2"
            onClick={handleExport}
            disabled={isExporting || selectedColumns.length === 0}
          >
            {isExporting ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Export en cours...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Exporter
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Column Selection */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-semibold">Colonnes à exporter</h4>
            <div className="flex gap-2">
              <button type="button" className="btn btn-xs btn-ghost" onClick={handleSelectAll}>
                Tout sélectionner
              </button>
              <button type="button" className="btn btn-xs btn-ghost" onClick={handleDeselectAll}>
                Tout désélectionner
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-base-100">
            {availableColumns.map((col) => (
              <label
                key={col.id}
                className="flex items-center gap-2 p-2 hover:bg-base-200 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm checkbox-primary"
                  checked={selectedColumns.includes(col.id)}
                  onChange={() => handleToggleColumn(col.id)}
                />
                <span className="text-sm truncate">{col.label}</span>
              </label>
            ))}
          </div>

          <p className="text-sm text-base-content/60 mt-1">
            {selectedColumns.length} colonne(s) sélectionnée(s)
          </p>
        </div>

        {/* Preview */}
        <div>
          <h4 className="font-semibold mb-2">Aperçu ({previewData.length} ligne(s))</h4>
          <div className="overflow-x-auto border rounded-lg">
            <table className="table table-sm">
              <thead>
                <tr className="bg-base-200">
                  {availableColumns
                    .filter((col) => selectedColumns.includes(col.id))
                    .map((col) => (
                      <th key={col.id} className="text-xs">
                        {col.label}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, idx) => (
                  <tr key={idx}>
                    {availableColumns
                      .filter((col) => selectedColumns.includes(col.id))
                      .map((col) => (
                        <td key={col.id} className="text-xs">
                          {typeof row[col.id] === "object"
                            ? JSON.stringify(row[col.id])
                            : String(row[col.id] || "")}
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
